import { useMemo } from 'react';

import type { Capabilities } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import { useGetIdTokenClaims } from '@platform/client/auth/use-get-id-token-claims';

import { useCylanceConsole } from '@apis/eloc/queries/cylance-console/use-cylance-console';
import { useCylanceConsoleLinks } from '@packages/hooks/use-cylance-console-links/use-cylance-console-links';
import { useOrganizationParam } from '@packages/hooks/use-organization-param';
import { useFeatureFlag } from '@packages/platform/feature-flags';
import { env } from '@packages/utils/env';

import type { AppSwitchMenuItem } from '../app-bar/toolbar/app-switch-menu';
import { useOrganizationAccess } from '../use-organization-access';

type AvailableMenuItem = AppSwitchMenuItem & {
  legacyCapability?: keyof Capabilities;
};

export const useAppSwitchMenuItems = (): AppSwitchMenuItem[] => {
  const { t } = useTranslation('app', { keyPrefix: 'appBar.appSwitch' });

  const orgId = useOrganizationParam();
  const [cylanceConsoleFF] = useFeatureFlag('CYLANCE_CONSOLE_LINKS');

  const {
    data: { legacyCapabilities, organization },
  } = useOrganizationAccess();
  const { data: cylanceConsole } = useCylanceConsole({
    enabled: Boolean(cylanceConsoleFF),
  });

  const idTokenClaims = useGetIdTokenClaims();

  const cylanceConsoleLinks = useCylanceConsoleLinks(
    cylanceConsole,
    idTokenClaims?.__raw,
  );

  const availableMenuItems: AvailableMenuItem[] = useMemo(() => {
    const { customerID } = organization ?? {};

    return [
      {
        text: t('riskPortal'),
        href: `${env.VITE_MANAGED_RISK_BASE_PATH}/?id=${orgId}`,
        legacyCapability: 'canAccessManageRisk',
      },
      {
        text: t('managedSecurityAwareness'),
        href: `https://sat.arcticwolf.com/Home/Dashboard?cid=${customerID}`,
        legacyCapability: 'canAccessManageAwareness',
      },
      ...cylanceConsoleLinks,
    ];
  }, [cylanceConsoleLinks, orgId, organization, t]);

  return useMemo(
    () =>
      availableMenuItems.reduce(
        (menuItems, { legacyCapability, ...menuItem }) => {
          if (
            !legacyCapability ||
            (legacyCapabilities && legacyCapabilities[legacyCapability])
          ) {
            menuItems.push(menuItem);
          }

          return menuItems;
        },
        [] as AppSwitchMenuItem[],
      ),
    [availableMenuItems, legacyCapabilities],
  );
};
